<template>
	<div
		:class="[
			'group relative flex  flex-col rounded-[8px] from-slate-400 to-slate-400/0 duration-300',
			disableHover ? '' : 'translate-y-0 hover:-translate-y-1',
			{
				'bg-gradient-to-t': mode == 'v',
				'bg-gradient-to-r': mode == 'h'
			}
		]"
	>
		<div
			:class="[
				'absolute bottom-0 left-0 right-0 top-0 rounded-[8px] duration-300',
				disableHover ? 'opacity-100' : 'opacity-0 group-hover:opacity-100',
				color ? '' : 'bg-gradient-to-tr from-primary to-primary-dark'
			]"
			:style="{
				backgroundColor: color
			}"
		></div>
		<div class="z-[1] flex flex-grow flex-col border-2 border-transparent">
			<div
				:class="[
					'group relative flex-grow overflow-hidden rounded-[7px]',
					color ? 'bg-white' : 'bg-white',
					{
						'flex flex-col': mode == 'v',
						'flex flex-col md:grid md:grid-cols-3': mode == 'h'
					}
				]"
			>
				<div
					:class="[
						'relative overflow-hidden',
						{
							'md:col-span-1': mode == 'h'
						}
					]"
				>
					<div
						:class="[
							'relative w-full overflow-hidden transition-transform duration-300',
							imageContainerClass,
							{
								'aspect-[16/9]': mode == 'v',
								'aspect-[16/9] md:h-full': mode == 'h'
							}
						]"
						:style="{
							backgroundImage: `url('/img/bg_pattern.jpg')`,
							backgroundRepeat: 'repeat',
							backgroundSize: '300px 300px'
						}"
					>
						<img
							v-if="coverImage"
							:src="$config.public.baseUrl + coverImage"
							:alt="$stringUtil.getLocalText(article.title)"
							:class="['h-full w-full object-cover']"
						/>
					</div>
				</div>

				<div
					:class="[
						'flex flex-1 flex-col',
						color ? '' : 'bg-gradient-to-b from-slate-50 to-white',
						{
							'md:col-span-2': mode == 'h'
						},
						{
							'px-3 pb-2.5 pt-3': size == 'sm',
							'px-3.5 pb-3 pt-3.5': size == 'md' || size == 'lg'
						}
					]"
					:style="{
						backgroundColor: color ? tinycolor(color).lighten(10).setAlpha(0.05).toString() : undefined
					}"
				>
					<div
						v-if="categories.length > 0 && !hideCategory"
						:class="[
							'flex flex-wrap',
							{
								'mb-3 gap-1.5': size == 'sm',
								'mb-3.5 gap-2': size == 'md' || size == 'lg'
							}
						]"
					>
						<span
							v-for="category in categories.slice(0, 1)"
							:key="category.id"
							class="bg-white relative flex overflow-hidden rounded px-1.5 py-1 text-xs font-medium"
						>
							<span
								class="absolute bottom-0 left-0 right-0 top-0 opacity-20"
								:style="{
									backgroundColor: tinycolor(category.color || '#6366f1')
										.brighten(10)
										.toString()
								}"
							></span>
							<span
								class="z-[1]"
								:style="{
									color: tinycolor(category.color || '#6366f1')
										.darken(15)
										.toString()
								}"
							>
								{{ $stringUtil.getLocalText(category.title) }}
							</span>
						</span>
						<span
							v-if="categories.length > 2"
							class="bg-white relative flex overflow-hidden rounded px-1.5 py-1 text-xs font-medium"
						>
							<span
								class="text-slate-700 bg-slate-700 absolute bottom-0 left-0 right-0 top-0 opacity-20"
							></span>
							<span dir="ltr" class="z-[1]"> +{{ categories.length - 2 }} </span>
						</span>
					</div>

					<h3
						:class="[
							'text-slate-900 line-clamp-2 font-bold',
							hideFooter ? '!mb-0' : 'mt-1',
							{
								'mb-5 text-sm': size == 'sm',
								'mb-6 text-lg': size == 'md' || size == 'lg'
							}
						]"
					>
						{{ $stringUtil.getLocalText(article.title) || "~" }}
					</h3>

					<div :class="[hideFooter ? 'h-0.5' : 'flex-grow']"></div>

					<div v-if="!hideFooter" class="flex items-center">
						<div
							v-if="article.published || article.created"
							class="text-slate-500 flex items-center text-xs"
						>
							<Icon name="mdi:calendar" class="me-1 h-[18px] w-[18px]" />
							<span class="font-medium">
								{{ moment.utc(article.published || article.created).fromNow() }}
							</span>
						</div>
						<div class="flex-grow"></div>
						<div v-if="article.read_time" class="text-slate-600 flex items-center text-xs">
							<Icon name="mdi:clock-outline" class="me-1 h-[18px] w-[18px]" />
							<span class="font-medium">
								{{
									$t("read_time_n", {
										n: $t(
											"n_minutes",
											{ n: Math.ceil(article.read_time / 60) },
											Math.ceil(article.read_time / 60)
										)
									})
								}}
							</span>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
import tinycolor from "tinycolor2"
import type { Post } from "~/models/post"

const moment = useMoment()
const categoryStore = useCategoryStore()

const props = withDefaults(
	defineProps<{
		article: Post
		size?: "sm" | "md" | "lg"
		mode?: "h" | "v"
		color?: string
		hideFooter?: boolean
		hideCategory?: boolean
		disableHover?: boolean
		imageContainerClass?: unknown
	}>(),
	{
		size: "md",
		mode: "v"
	}
)

const categories = computed(
	() => categoryStore.categories?.filter((c) => props.article?.category_ids?.includes?.(c.id)) || []
)

const coverImage = computed(() => {
	if (!props.article.covers?.[0]?.id) {
		return null
	}
	if (props.size == "lg") {
		return props.article.covers[0].path
	} else {
		return props.article.covers[0].path_thumb2
	}
})
</script>
